import React, { useEffect, useState } from "react";
import { IconTarget } from "@tabler/icons-react";
import { ActiveToggle } from "pages/TopicViewer/components/ActiveToggle";
import {
  apiRequest,
  Converse,
  MicRecorderProvider,
  ModusProvider,
  Topic,
  useAccount,
  useGlobal,
  useModus,
  VojomoLoader,
  MemberMarker
} from "@vojomo-core/shared";
import { ViewSelector } from "components/ViewSelector/ViewSelector";
import { PersonalQBR } from "./PersonalQBR";
import { QBRProvider, useQBR } from "./use-qbr";
import { TeamQBR } from "./TeamQBR";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { PanelLeftClose, PanelRightClose } from "lucide-react";
import { Button } from "antd";
import "./QBR.css";

export const QBR: React.FC = () => {
  const [topicId, setTopicId] = useState<string>(null);

  useEffect(() => {
    const f = async () => {
      const data = await apiRequest<Topic>({
        method: "GET",
        subRoute: "roadmap",
        path: "/get_building_block?block_name=1 Year Objectives&team_mode=true"
      });
      setTopicId(data["_id"]);
    };

    f();
  }, []);

  if (!topicId) {
    return <></>;
  }

  return (
    <ModusProvider agentName="QBRScoringAgency" refId={topicId}>
      <MicRecorderProvider>
        <QBRProvider>
          <QBRInternal />
        </QBRProvider>
      </MicRecorderProvider>
    </ModusProvider>
  );
};

const QBRInternal: React.FC = () => {
  const { view } = useGlobal();
  const { companyName } = useAccount();
  const { sendMessage } = useModus();
  const [chatOpen, setChatOpen] = useState(true);

  const {
    qbrData,
    isActive,
    setIsActive,
    hasTable,
    personalScoresList,
    fetchQBRData
  } = useQBR();

  if (!qbrData || !personalScoresList || isActive == null) {
    return <VojomoLoader />;
  }

  if (!hasTable) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-[#7C788A]">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-32 w-32 items-center justify-center rounded-full border border-[#E6E3EE]">
            <IconTarget size={56} className="text-[#7C788A]" />
          </div>
          <MemberMarker />
          <p>No Annual Objectives set for {companyName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-[#4B4758]">
              Annual Objective Scoring
            </h2>

            <MemberMarker />

            {/* <div className="flex flex-wrap items-center gap-2 text-sm text-[#7C788A]">
              <div className="flex items-center gap-2">
                <IconCalendar size={18} className="text-[#9A96A8]" />
                <span>Jan. 1 - Dec. 31, 2026</span>
              </div>
              <span className="rounded-full bg-[#E9E7F3] px-3 py-1 text-xs font-semibold text-[#5E5A6E]">
                Q1
              </span>
            </div> */}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E6E3EE] bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-[280px] flex-1">
            <div className="text-sm font-semibold text-white">
              Scoring Legend - Q1 Targets
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#7C788A]">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-[#2FB764] px-3 py-1 text-sm font-semibold text-[#FFFFFF]">
                  On Track
                </span>
                <span>2.5+</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-[#F3C623] px-3 py-1 text-sm font-semibold text-[#000000]">
                  Behind
                </span>
                <span>1.875+</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-[#D66E2E] px-3 py-1 text-sm font-semibold text-[#FFFFFF]">
                  Far Behind
                </span>
                <span>1.25+</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-[#E34A4A] px-3 py-1 text-sm font-semibold text-[#FFFFFF]">
                  Red Flag
                </span>
                <span>&lt; 1.25</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-[#7C788A]">
              Scores represent progress from 0 to 10 (0% to 100%). Target
              thresholds adjust each quarter to reflect expected annual
              progress.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-8">
            {view === "my" && isActive != null && (
              <ActiveToggle
                disabled={false}
                initialValue={isActive}
                onLabel={"Contributed To Team"}
                offLabel={"Not Contributed To Team"}
                onToggle={async (val) => {
                  setIsActive(val);
                  await apiRequest({
                    subRoute: "roadmap",
                    path: `/update_objective_score`,
                    method: "POST",
                    body: {
                      payload: { active: val },
                      topicId: qbrData.topic_id
                    }
                  });
                }}
              />
            )}
            <ViewSelector />
          </div>
        </div>
      </div>
      <div className="qbr-panels-wrapper">
        {view === "my" && !chatOpen && (
          <div className="side-rail" onClick={() => setChatOpen(true)}>
            <div className="rail-content">
              <PanelRightClose className="rail-icon" />
              <span className="rail-label rail-label-vertical">Open Vo</span>
            </div>
          </div>
        )}

        <PanelGroup
          key={`qbr-panel-${chatOpen ? "chat" : "nochat"}-${view}`}
          direction="horizontal"
          className="resizable-group"
        >
          {view === "my" && chatOpen && (
            <>
              <Panel
                defaultSize={25}
                minSize={20}
                maxSize={40}
                className="panel chat-panel"
              >
                <div className="chat-panel-shell">
                  <div className="panel-header">
                    <div className="panel-title">
                      Vo, your Scoring Assistant
                    </div>
                    <Button
                      type="text"
                      size="small"
                      className="icon-button-muted"
                      onClick={() => setChatOpen(false)}
                    >
                      <PanelLeftClose className="icon-16" />
                    </Button>
                  </div>
                  <div className="chat-panel-content">
                    <Converse
                      size={250}
                      fontSizeOverride="14px"
                      orbFontSizeOverride="10px"
                      showTranscriptIcon={true}
                      placeHolder={null}
                      showSquigly={false}
                      squiglySize={null}
                      onProcess={async (transcript: string) => {
                        await sendMessage(transcript);
                        await fetchQBRData();
                      }}
                    />
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle className="resize-handle" />
            </>
          )}
          <Panel
            defaultSize={view === "my" && chatOpen ? 75 : 100}
            className="panel qbr-table-panel"
            style={{ overflow: "visible" }}
          >
            {view === "my" ? <PersonalQBR /> : <TeamQBR />}
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
};
